/* eslint-disable react/no-unused-prop-types */
import { useCallback, useMemo, useState } from 'react';
import { DatePickerModal, RenderIf } from 'react-rainbow-components';
import { useReduxForm } from '@rainbow-modules/hooks';
import Close from 'components/icons/close';
import Plus from 'components/icons/plus';
import { getFormatter } from 'data/services/date/formatter';
import { CalendarHoliday } from './parseIcsHolidays';
import {
    Container,
    DateButton,
    DateControl,
    DuplicateInfo,
    EmptyLabel,
    HolidayRow,
    NameInput,
    StyledButtonIcon,
} from './styled';

interface CalendarHolidayInputProps {
    id?: string;
    className?: string;
    value?: CalendarHoliday[];
    onChange?: (value: CalendarHoliday[]) => void;
}

const dateFormatter = getFormatter('en-US', { month: 'short', day: 'numeric' });

const DuplicateIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 5.333h.007" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7.333 8H8v2.667h.667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const toDate = ({ month, day }: CalendarHoliday) => new Date(
    new Date().getFullYear(),
    month - 1,
    day,
);

const createHoliday = (): CalendarHoliday => {
    const today = new Date();
    return {
        id: globalThis.crypto?.randomUUID?.()
            || `holiday-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: '',
        month: today.getMonth() + 1,
        day: today.getDate(),
    };
};

const CalendarHolidayInput = (props: CalendarHolidayInputProps) => {
    const {
        value: valueInProps,
        onChange = () => {},
        id,
        className,
    } = useReduxForm(props);
    const holidays: CalendarHoliday[] = useMemo(() => valueInProps || [], [valueInProps]);
    const [editingId, setEditingId] = useState<string>();

    const duplicateIds = useMemo(() => {
        const seenDates = new Set<string>();
        return holidays.reduce((duplicates, holiday) => {
            const key = `${holiday.month}-${holiday.day}`;
            if (seenDates.has(key)) {
                duplicates.add(holiday.id);
            } else {
                seenDates.add(key);
            }
            return duplicates;
        }, new Set<string>());
    }, [holidays]);

    const editingHoliday = useMemo(
        () => holidays.find((holiday) => holiday.id === editingId),
        [editingId, holidays],
    );

    const updateHoliday = useCallback(
        (holidayId: string, changes: Partial<CalendarHoliday>) => onChange(
            holidays.map(
                (holiday) => (holiday.id === holidayId ? { ...holiday, ...changes } : holiday),
            ),
        ),
        [holidays, onChange],
    );

    const handleAdd = useCallback(
        (afterIndex: number) => onChange([
            ...holidays.slice(0, afterIndex + 1),
            createHoliday(),
            ...holidays.slice(afterIndex + 1),
        ]),
        [holidays, onChange],
    );

    const handleRemove = useCallback(
        (holidayId: string) => onChange(holidays.filter((holiday) => holiday.id !== holidayId)),
        [holidays, onChange],
    );

    const handleDateChange = useCallback(
        (date: Date) => {
            if (editingId) {
                updateHoliday(editingId, { month: date.getMonth() + 1, day: date.getDate() });
            }
            setEditingId(undefined);
        },
        [editingId, updateHoliday],
    );

    return (
        <Container id={id} className={className}>
            <RenderIf isTrue={holidays.length === 0}>
                <HolidayRow>
                    <EmptyLabel>No holidays</EmptyLabel>
                    <StyledButtonIcon
                        size="small"
                        borderRadius="semi-rounded"
                        icon={<Plus />}
                        onClick={() => handleAdd(-1)}
                    />
                </HolidayRow>
            </RenderIf>
            {holidays.map((holiday, index) => (
                <HolidayRow key={holiday.id}>
                    <NameInput
                        value={holiday.name}
                        placeholder="Holiday name"
                        borderRadius="semi-square"
                        hideLabel
                        label="Holiday name"
                        $duplicated={duplicateIds.has(holiday.id)}
                        onChange={(event) => updateHoliday(holiday.id, {
                            name: event.target.value,
                        })}
                    />
                    <DateControl>
                        <DateButton
                            type="button"
                            $duplicated={duplicateIds.has(holiday.id)}
                            onClick={() => setEditingId(holiday.id)}
                        >
                            {dateFormatter.format(toDate(holiday))}
                        </DateButton>
                        <RenderIf isTrue={duplicateIds.has(holiday.id)}>
                            <DuplicateInfo
                                icon={<DuplicateIcon />}
                                tooltip="Duplicated"
                                variant="base"
                            />
                        </RenderIf>
                    </DateControl>
                    <StyledButtonIcon
                        size="small"
                        borderRadius="semi-rounded"
                        icon={<Close />}
                        onClick={() => handleRemove(holiday.id)}
                    />
                    <StyledButtonIcon
                        size="small"
                        borderRadius="semi-rounded"
                        icon={<Plus />}
                        onClick={() => handleAdd(index)}
                    />
                </HolidayRow>
            ))}
            <DatePickerModal
                isOpen={Boolean(editingHoliday)}
                value={editingHoliday ? toDate(editingHoliday) : undefined}
                borderRadius="semi-rounded"
                onChange={handleDateChange}
                onRequestClose={() => setEditingId(undefined)}
            />
        </Container>
    );
};

export default CalendarHolidayInput;
